import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  AreaChart,
  Area,
} from "recharts";

import duration from "dayjs/plugin/duration";
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

export default function PDFLineChartComponent({
  maxValuebps = "auto",
  data = [],
  yOptions = {},
  xOptions = {},
  gridOptions = {},
  areaChartOptions = {},
  areaOptions = {},
  mainColor = null,
  mainLineColor = null,
  referenceLines = [], //{name,value,opt}
  theme = {},
  tickOptions = {},
  type = 0, // 0: "bps", 1: "percent", 2: "ms", 3: "dBm"
  invertNegativeValues = false,
}) {
  const MAIN_COLOR = mainColor || theme.palette.cardGraphic.graphColor.blue;
  const MAIN_LINE_COLOR =
    mainLineColor || theme.palette.cardGraphic.graphLineColor.blue;

  const timestamps = data?.map((item) => item.clock);
  const start = dayjs.unix(timestamps[0]);
  const end = dayjs.unix(timestamps[timestamps.length - 1]);
  const dateFormat = end.diff(start, "days") > 6 ? "DD/MM" : "DD/MM HH:mm";

  const getCustomTicks = (data, options = {}) => {
    if (!data || data.length === 0) return [];

    const {
      maxPointsShort = 12, // Máximo de ticks para ≤6 dias
      maxPointsLong = 22, // Máximo de ticks para >6 dias
      showFirst = true, // Mostrar primeiro ponto
      showLast = true, // Mostrar último ponto
    } = options;

    // Extrai timestamps e calcula duração total em dias
    const timestamps = data?.map((item) => item?.clock);
    const totalDays = end.diff(start, "day", true); // Dias com fração

    // Determina a estratégia de seleção de ticks
    const useDailyTicks = totalDays > 6;
    const maxTicks = useDailyTicks ? maxPointsLong : maxPointsShort;

    // Seleciona ticks baseado na estratégia
    let selectedTicks = [];

    if (useDailyTicks) {
      // Modo diário (1 tick por dia ou com espaçamento dinâmico se necessário)
      const totalDaysInt = Math.ceil(totalDays);
      const dayInterval = Math.max(1, Math.ceil(totalDaysInt / (maxTicks - 1)));

      const daysSet = new Set();
      for (let day = 0; day <= totalDaysInt; day += dayInterval) {
        daysSet.add(day);
      }

      timestamps.forEach((ts) => {
        const currentDay = dayjs.unix(ts).diff(start, "day");
        if (daysSet.has(currentDay)) {
          selectedTicks.push(ts);
          daysSet.delete(currentDay); // Evita duplicatas
        }
      });
    } else {
      // Modo uniforme (ticks igualmente espaçados)
      const interval = Math.max(
        1,
        Math.floor((timestamps.length - 1) / (maxTicks - 1)),
      );
      for (let i = 0; i < timestamps.length; i += interval) {
        selectedTicks.push(timestamps[i]);
      }
    }

    // Garante primeiro e último ticks
    if (showFirst && !selectedTicks.includes(timestamps[0])) {
      selectedTicks.unshift(timestamps[0]);
    }
    if (
      showLast &&
      !selectedTicks.includes(timestamps[timestamps.length - 1])
    ) {
      selectedTicks.push(timestamps[timestamps.length - 1]);
    }

    // Remove duplicatas e ordena
    selectedTicks = [...new Set(selectedTicks)].sort((a, b) => a - b);

    // Limita ao número máximo de ticks
    if (selectedTicks.length > maxTicks) {
      const step = Math.ceil(selectedTicks.length / maxTicks);
      const limitedTicks = [];
      for (let i = 0; i < selectedTicks.length; i += step) {
        limitedTicks.push(selectedTicks[i]);
      }
      selectedTicks = limitedTicks;

      // Garante último tick novamente (pode ter sido cortado)
      if (
        showLast &&
        !selectedTicks.includes(timestamps[timestamps.length - 1])
      ) {
        selectedTicks.push(timestamps[timestamps.length - 1]);
      }
    }

    return selectedTicks;
  };

  const customTicks = getCustomTicks(data, tickOptions);

  const mergedYOptions = {
    // tickFormatter: formatBytes,
    tickFormatter: getTickFormatter(type, invertNegativeValues),
    interval: 0,
    domain: [0, type === 1 ? 100 : maxValuebps],
    tick: {
      fill: theme.palette.black.main,
      opacity: 0.7,
    },
    style: { fontSize: "10px", fontWeight: 500 },
    allowDataOverflow: true,
    ...yOptions,
  };

  const mergedXOptions = {
    dataKey: "clock",
    // tickFormatter: (clock) =>
    //   dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
    //interval: calcInterval(),
    tickFormatter: (clock) => dayjs.unix(clock).tz(timeZone).format(dateFormat),
    style: { fontSize: "10px", fontWeight: 500, marginRight: "50px" },
    tick: {
      fill: theme.palette.black.main,
      opacity: 0.7,
    },
    ticks: customTicks,
    ...xOptions,
  };

  const mergedGridOptions = {
    stroke: MAIN_LINE_COLOR,
    strokeOpacity: 0.5,
    vertical: false,
    strokeDasharray: "3",
    ...gridOptions,
  };

  const mergedAreaChartOptions = {
    margin: {
      top: 20,
      right: 20,
      left: 10,
      bottom: 0,
    },

    ...areaChartOptions,
  };

  const mergedAreaOptions = {
    type: "monotone",
    dataKey: "value",
    name: "Tráfego",
    strokeWidth: 1,
    stroke: MAIN_COLOR,
    fill: `default-gradient-${MAIN_COLOR}`,
    fillOpacity: 1,
    stackId: 1,
    dot: false,
    isAnimationActive: false,
    ...areaOptions,
  };

  return (
    // <ResponsiveContainer ref={containerRef} width="100%" height="20%">
    <Box>
      <AreaChart data={data} {...mergedAreaChartOptions}>
        <defs>
          <linearGradient
            id={`default-gradient-${MAIN_COLOR}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="80%" stopColor={MAIN_LINE_COLOR} stopOpacity={0.5} />
            <stop
              offset="100%"
              stopColor={MAIN_COLOR}
              stopOpacity={theme.palette.mode == "dark" ? 0 : 0.2}
            />
          </linearGradient>
        </defs>
        <CartesianGrid {...mergedGridOptions} />
        <XAxis {...mergedXOptions} />
        <YAxis {...mergedYOptions} />

        {Array.isArray(data) && data.length > 0 && (
          <Area
            {...mergedAreaOptions}
            dataKey={(d) =>
              invertNegativeValues ? Math.abs(d.value) : d.value
            }
            fill={`url(#default-gradient-${MAIN_COLOR})`}
          />
        )}
        {referenceLines?.map((line, index) => (
          <ReferenceLine
            key={`ref-line-${index}`}
            stroke={line.opt?.stroke || "red"}
            strokeWidth={line.opt?.strokeWidth || 1.5}
            y={line.value}
            label={{
              value: line.name,
              fill: line.opt?.label?.fill || "red",
              fontSize: line.opt?.label?.fontSize || "12px",
              fontWeight: line.opt?.label?.fontWeight || 500,
              position: line.opt?.label?.position || "insideRight",
              color: line.opt?.label?.color || theme.palette.primary.main,
              ...line.opt?.label,
            }}
            {...line.opt}
          />
        ))}
      </AreaChart>

      {/* </ResponsiveContainer> */}
    </Box>
  );
}

// Conversão de dados
const formatBytes = (bps) => {
  if (bps === 0) return "0 bps";
  const k = 1000;
  const sizes = ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];
  // Força i a ser no mínimo 0
  const i = Math.max(0, Math.floor(Math.log(bps) / Math.log(k)));
  const value = bps / Math.pow(k, i);
  return `${value.toFixed(2)} ${sizes[i]}`;
};

const getTickFormatter = (type, invertNegativeValues) => {
  switch (type) {
    case 1:
      return (value) => `${value} %`;
    case 2:
      return (value) => `${value ? value.toFixed(4) : value} ms`;
    case 3:
      return (value) =>
        `${invertNegativeValues ? "-" : ""}${
          value ? value.toFixed(2) : value
        } dBm`;
    default:
      return formatBytes;
  }
};
